import * as styles from "./VulSourceItemListTable.module.css";
import {ColumnsType} from "antd/es/table";
import type {TableRowSelection} from "antd/es/table/interface";
import VulSourceItem from "../../domain/VulSourceItem";
import {Badge, Button, Checkbox, ConfigProvider, Dropdown, Form, MenuProps, message, Modal, Table, Tag} from "antd";
import {CodeOutlined, FileAddOutlined, HistoryOutlined, ReloadOutlined} from "@ant-design/icons";
import ItemVoteCompact from "./ItemVoteCompact";
import {formatDateTime, formatInteger} from "../../utils/FormatUtils";
import * as globalStyles from "../App.module.css";
import {createContext, memo, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState} from "react";
import {isColumnVisible, TableConfig} from "../tableconfig/TableConfig";
import CreateTicketModal from "../ticket/CreateTicketModal";
import {Link, useNavigate} from "react-router-dom";
import QueryOptions from "../../sal-ui/QueryOptions";
import PagedResult from "../../service/PagedResult";
import {AppContextContext, AttributeDefinitionServiceContext, VulSourceItemServiceContext} from "../../Contexts";
import AttributeDefinition from "../../domain/AttributeDefinition";
import {TableHandler, TableUiConfig} from "../../sal-ui/TableHandler";
import {descendingFirstSortDirections} from "../../sal-ui/TableSortDirections";
import {VulSourceItemChangeType} from "../../domain/VulSourceItemChangeType";
import VulSourceItemChangeLog from "../../domain/VulSourceItemChangeLog";
import VulSourceChangeLogListDrawer from "./VulSourceChangeLogListDrawer";
import {FiExternalLink} from "react-icons/fi";
import {useForm} from "antd/es/form/Form";
import {Permission} from "../../domain/auth/Permission";
import VulSourceItemMassAiExtractModal from "./VulSourceItemMassAiExtractModal";
import VulSourceItemPreview from "./VulSourceItemPreview";

interface Props {
    persistentIdent: string;
    items: PagedResult<VulSourceItem>;
    tableHandler: TableHandler;
    tableUiConfig: TableUiConfig;
    setTableUiConfig: (uiConfig: TableUiConfig) => void;
    aiSummaryGroups?: VulSourceItemListGroup[];
    rowSelection?: TableRowSelection<VulSourceItem>;
}

export interface VulSourceItemListGroup {
    value: string;
    title: string;
    itemCount: number;
    itemIds?: string[];
    basis?: string;
    ungrouped?: boolean;
}

interface GroupHeaderRow {
    group: VulSourceItemListGroup;
    visibleItemCount: number;
}

type TableBodyRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
    "data-row-key"?: string;
};

const GroupHeaderContext = createContext({headers: new Map<string, GroupHeaderRow>(), columnCount: 0});
const groupedTableComponents = {body: {row: GroupedTableBodyRow}};

function VulSourceItemListTable(props: Props) {
    const appContext = useContext(AppContextContext);
    const vulSourceItemService = useContext(VulSourceItemServiceContext);
    const attributeDefinitionService = useContext(AttributeDefinitionServiceContext);
    const navigate = useNavigate();
    const [createTicketFormVisible, setCreateTicketFormVisible] = useState<boolean>(false);
    const [vulSourceItem, setVulSourceItem] = useState<VulSourceItem>();
    const [attributeDefinitions, setAttributeDefinitions] = useState<AttributeDefinition[]>();
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [drawerChangeLogs, setDrawerChangeLogs] = useState<VulSourceItemChangeLog[]>();
    const [changeLogDrawerOpen, setChangeLogDrawerOpen] = useState(false);
    const [changeLogDrawerItem, setChangeLogDrawerItem] = useState<VulSourceItem>();
    const [extractAndSaveModalOpen, setExtractAndSaveModalOpen] = useState(false);
    const [aiExtractItemsModalOpen, setAiExtractItemsModalOpen] = useState(false);
    const [voteRevision, forceUpdate] = useReducer(x => x + 1, 0);
    const reloadTimerRef = useRef<ReturnType<typeof setInterval>>();
    const onCreateTicket = useCallback((item: VulSourceItem) => {
        setCreateTicketFormVisible(true);
        setVulSourceItem(item);
    }, []);
    const onShowChanges = useCallback((item: VulSourceItem) => {
        setDrawerLoading(true);

        vulSourceItemService.getChangeLogList(item)
            .then(changeLogs => {
                setDrawerChangeLogs(changeLogs.filter(value => value.type === VulSourceItemChangeType.ATTRIBUTES));
                setChangeLogDrawerItem(item);
                setChangeLogDrawerOpen(true);
            })
            .finally(() => setDrawerLoading(false));
    }, [vulSourceItemService]);
    const updateItems = useCallback((item: VulSourceItem) => {
        props.items.data.forEach(value => {
            if (value.id === item.id) {
                value.flags = item.flags;
                value.flagStats = item.flagStats;
            }
        });

        forceUpdate();
    }, [props.items]);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        attributeDefinitionService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => setAttributeDefinitions(value.data));
    }, []);

    useEffect(() => {
        if (props.tableUiConfig?.autoRefreshEnabled && props.tableUiConfig?.autoRefreshInterval) {
            clearReloadTimer();

            reloadTimerRef.current = setInterval(props.tableHandler.reload, props.tableUiConfig?.autoRefreshInterval * 1000);
        } else {
            clearReloadTimer();
        }

        return function cleanup() {
            clearReloadTimer();
        };
    }, [props.tableUiConfig]);

    const columns: ColumnsType<VulSourceItem> = [
        {
            dataIndex: "id",
            title: "ID",
            width: 80,
            align: "center",
            render: renderId
        },
        {
            dataIndex: "description",
            title: "Description",
            render: renderDescription
        },
        {
            dataIndex: "created",
            title: "Created at",
            width: 160,
            align: "center",
            sorter: true,
            sortDirections: descendingFirstSortDirections,
            render: value => formatDateTime(value)
        },
        {
            dataIndex: "lastModified",
            title: "Data modification",
            width: 160,
            align: "center",
            sorter: true,
            sortDirections: descendingFirstSortDirections,
            render: value => formatDateTime(value)
        },
        {
            dataIndex: "lastModifiedAttributes",
            title: "Attributes modification",
            width: 160,
            align: "center",
            sorter: true,
            sortDirections: descendingFirstSortDirections,
            render: value => formatDateTime(value)
        },
        {
            dataIndex: "sourcePublished",
            title: "Published",
            width: 160,
            align: "center",
            sorter: true,
            sortDirections: descendingFirstSortDirections,
            defaultSortOrder: "descend",
            render: value => formatDateTime(value)
        },
        {
            dataIndex: "sourceLastUpdate",
            title: "Last update",
            width: 160,
            align: "center",
            sorter: true,
            sortDirections: descendingFirstSortDirections,
            render: value => formatDateTime(value)
        },
    ];

    const menuItems: MenuProps['items'] = [
        ...(appContext.user.isSystemAdmin() ? [{
            label: 'Extract attributes',
            title: 'Extract attributes from all items of current filter.',
            key: 'extract-attributes',
        }] : []),
        ...(appContext.user.isSystemAdmin() || appContext.user.hasPermission(Permission.AI_EXTRACTOR__MANAGE_ORG) ? [{
            label: 'AI extract items',
            title: 'AI extract all items of current filter using selected AI extractors.',
            key: 'ai-extract-items',
        }] : []),
    ];

    const handleMenuClick: MenuProps['onClick'] = (e) => {
        switch (e.key) {
            case "extract-attributes":
                setExtractAndSaveModalOpen(true);
                break;
            case "ai-extract-items":
                setAiExtractItemsModalOpen(true);
                break;
        }
    };

    const menuProps = {
        items: menuItems,
        onClick: handleMenuClick,
    };
    const visibleColumns = columns.filter(column => isColumnVisible(props.tableUiConfig.visibleColumns, column));
    const showAiSummaryGroups = (props.tableUiConfig.showAiSummaryGroups ?? false) && (props.aiSummaryGroups?.length ?? 0) > 0;
    const tableData = useMemo(() => showAiSummaryGroups
        ? buildGroupedItems(props.items?.data ?? [], props.aiSummaryGroups ?? [])
        : props.items?.data, [showAiSummaryGroups, props.items, props.aiSummaryGroups]);
    const groupHeaderByItemId = useMemo(() => showAiSummaryGroups
        ? buildGroupHeaderByItemId(tableData ?? [], props.aiSummaryGroups ?? [])
        : new Map<string, GroupHeaderRow>(), [showAiSummaryGroups, tableData, props.aiSummaryGroups]);
    const columnCount = visibleColumns.length + (props.rowSelection ? 1 : 0);
    const groupHeaderContext = useMemo(() => ({headers: groupHeaderByItemId, columnCount}), [groupHeaderByItemId, columnCount]);

    return (
        <>
            <div className={globalStyles["common__top-button-bar"]}>
                <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={props.tableHandler.reload}/>

                <TableConfig
                    columns={columns}
                    value={props.tableUiConfig}
                    showAiAnalyticsOption={true}
                    showAiSummaryGroupsOption={(props.aiSummaryGroups?.length ?? 0) > 0}
                    onChange={uiConfig => {
                        props.setTableUiConfig(uiConfig);

                        props.tableHandler.saveUiConfig(uiConfig);
                    }}
                />

                {menuItems.length > 0 &&
                    <Dropdown menu={menuProps} trigger={["click"]}>
                        <Button icon={<CodeOutlined/>} className={"btn-seamless"} title={"Special actions"}/>
                    </Dropdown>
                }
            </div>

            <GroupHeaderContext.Provider value={groupHeaderContext}>
                <Table className={styles.table}
                       showSorterTooltip={false}
                       loading={props.tableHandler.loading}
                       dataSource={tableData}
                       size="middle"
                       onChange={props.tableHandler.onTableChange}
                       pagination={props.tableHandler.pagination}
                       onRow={onRow}
                       rowKey="id"
                       rowSelection={props.rowSelection}
                       columns={visibleColumns}
                       components={showAiSummaryGroups ? groupedTableComponents : undefined}
                />
            </GroupHeaderContext.Provider>

            <CreateTicketModal
                visible={createTicketFormVisible}
                onTicketCreate={() => setCreateTicketFormVisible(false)}
                vulSourceItem={vulSourceItem}
                onCancel={() => setCreateTicketFormVisible(false)}
            />

            <ExtractAndSaveAttributesModal
                open={extractAndSaveModalOpen}
                onClose={() => {
                    setExtractAndSaveModalOpen(false);

                    props.tableHandler.reload();
                }}
                itemCount={props.tableHandler.total()}
                rqbQuery={props.tableHandler.queryOptions.rqbQuery}
            />

            <VulSourceItemMassAiExtractModal
                open={aiExtractItemsModalOpen}
                onClose={() => {
                    setAiExtractItemsModalOpen(false);

                    props.tableHandler.reload();
                }}
                itemCount={props.tableHandler.total()}
                rqbQuery={props.tableHandler.queryOptions.rqbQuery}
                orderBy={props.tableHandler.queryOptions.orderBy}
            />

            <VulSourceChangeLogListDrawer item={changeLogDrawerItem} changeLogs={drawerChangeLogs} open={changeLogDrawerOpen} onClose={() => setChangeLogDrawerOpen(false)}/>
        </>
    );

    function renderId(value: string, item: VulSourceItem) {
        if (item.isReadable(appContext.user)) {
            return <Link to={`/vul-source-items/${value}`}>
                <span title={value}>{value.substring(0, 2) + '...' + value.substring(value.length - 2)}</span>
            </Link>;
        }

        return <span title={value}>{value.substring(0, 2) + '...' + value.substring(value.length - 2)}</span>;
    }

    function onRow(vulSourceItem: VulSourceItem) {
        return {onDoubleClick: () => navigate(`/vul-source-items/${vulSourceItem.id}`)};
    }

    function clearReloadTimer() {
        if (reloadTimerRef.current === undefined) {
            return;
        }

        clearInterval(reloadTimerRef.current);
        reloadTimerRef.current = undefined;
    }

    function renderDescription(_: any, item: VulSourceItem) {
        return <ItemDescription
            item={item}
            attributeDefinitions={attributeDefinitions}
            showAiAnalytics={props.tableUiConfig.showAiAnalytics ?? true}
            drawerLoading={drawerLoading}
            voteRevision={voteRevision}
            onCreateTicket={onCreateTicket}
            onShowChanges={onShowChanges}
            onVote={updateItems}
        />;
    }

}

function renderGroupHeader(row: GroupHeaderRow) {
    const hiddenItemCount = Math.max(row.group.itemCount - row.visibleItemCount, 0);

    return (
        <div className={styles["group-header"]}>
            <div className={styles["group-header-main"]}>
                <span className={styles["group-header-title"]}>{row.group.title}</span>
                <Tag>{formatGroupItemCount(row.group.itemCount)}</Tag>
                {hiddenItemCount > 0 && <Tag>{formatGroupItemCount(row.visibleItemCount)} shown</Tag>}
            </div>
            {row.group.basis && <div className={styles["group-header-basis"]}>{row.group.basis}</div>}
        </div>
    );
}

function GroupedTableBodyRow(rowProps: TableBodyRowProps) {
    const {headers, columnCount} = useContext(GroupHeaderContext);
    const itemId = rowProps["data-row-key"];
    const groupHeader = itemId ? headers.get(itemId) : undefined;
    const itemRow = <tr {...rowProps}/>;

    if (!groupHeader) {
        return itemRow;
    }

    return (
        <>
            <tr className={styles["group-header-row"]}>
                <td colSpan={columnCount}>{renderGroupHeader(groupHeader)}</td>
            </tr>
            {itemRow}
        </>
    );
}

function renderExternalLink(item: VulSourceItem) {
    if (!item?.attributes?.['link']) {
        return null;
    }

    return <Button className={"btn-seamless"} title={"External link"} icon={<FiExternalLink className={"anticon"}/>} onClick={() => window.open(item.attributes['link'], '_blank')}/>;
}

interface ItemDescriptionProps {
    item: VulSourceItem;
    attributeDefinitions?: AttributeDefinition[];
    showAiAnalytics: boolean;
    drawerLoading: boolean;
    // Voting updates the existing item object, so its reference alone cannot invalidate the preview.
    voteRevision: number;
    onCreateTicket: (item: VulSourceItem) => void;
    onShowChanges: (item: VulSourceItem) => void;
    onVote: (item: VulSourceItem) => void;
}

const ItemDescription = memo(function ItemDescription({item, attributeDefinitions, showAiAnalytics, drawerLoading, onCreateTicket, onShowChanges, onVote}: ItemDescriptionProps) {
    const appContext = useContext(AppContextContext);
    const metadataExtra = <>
        {item.changedItem && <Badge count={"Updated"} style={{marginLeft: 12}} color={"blue"} title={`Item created at ${formatDateTime(item.created)}.`}/>}

        <div className={styles['action-bar']}>
            {renderExternalLink(item)}

            {appContext.user.hasPermission(Permission.TICKET__MANAGE) &&
                <Button icon={<FileAddOutlined/>}
                        size={"small"}
                        className={"btn-seamless"}
                        title={"Create ticket"}
                        onClick={() => onCreateTicket(item)}/>
            }

            {item.changedItem &&
                <Button icon={<HistoryOutlined/>}
                        size={"small"}
                        className={"btn-seamless"}
                        title={"Show changes"}
                        disabled={drawerLoading}
                        onClick={() => onShowChanges(item)}
                />
            }

            {item.isReadable(appContext.user) && <ItemVoteCompact onVote={onVote} item={item}/>}
        </div>
    </>;

    return <VulSourceItemPreview
        item={item}
        attributeDefinitions={attributeDefinitions}
        showAiAnalytics={showAiAnalytics}
        framed={true}
        metadataExtra={metadataExtra}
    />;
});

function buildGroupedItems(items: VulSourceItem[], groups: VulSourceItemListGroup[]): VulSourceItem[] {
    const groupByItemId = buildGroupByItemId(groups);
    const itemsByGroup = new Map<string, VulSourceItem[]>();
    const ungroupedGroup = groups.find(group => group.ungrouped);

    groups.forEach(group => itemsByGroup.set(group.value, []));

    items.forEach(item => {
        const group = groupByItemId.get(item.id) ?? ungroupedGroup;

        if (!group) {
            return;
        }

        itemsByGroup.get(group.value)?.push(item);
    });

    const groupedItems: VulSourceItem[] = [];

    groups.forEach(group => {
        const groupItems = itemsByGroup.get(group.value) ?? [];

        if (groupItems.length === 0) {
            return;
        }

        groupedItems.push(...groupItems);
    });

    return groupedItems;
}

function buildGroupHeaderByItemId(items: VulSourceItem[], groups: VulSourceItemListGroup[]) {
    const groupByItemId = buildGroupByItemId(groups);
    const visibleItemCountByGroup = new Map<string, number>();
    const firstItemIdByGroup = new Map<string, string>();
    const ungroupedGroup = groups.find(group => group.ungrouped);

    items.forEach(item => {
        const group = groupByItemId.get(item.id) ?? ungroupedGroup;

        if (!group) {
            return;
        }

        visibleItemCountByGroup.set(group.value, (visibleItemCountByGroup.get(group.value) ?? 0) + 1);

        if (!firstItemIdByGroup.has(group.value)) {
            firstItemIdByGroup.set(group.value, item.id);
        }
    });

    const groupHeaderByItemId = new Map<string, GroupHeaderRow>();

    groups.forEach(group => {
        const firstItemId = firstItemIdByGroup.get(group.value);

        if (!firstItemId) {
            return;
        }

        groupHeaderByItemId.set(firstItemId, {
            group,
            visibleItemCount: visibleItemCountByGroup.get(group.value) ?? 0
        });
    });

    return groupHeaderByItemId;
}

function buildGroupByItemId(groups: VulSourceItemListGroup[]) {
    const groupByItemId = new Map<string, VulSourceItemListGroup>();

    groups
        .filter(group => !group.ungrouped)
        .forEach(group => {
            group.itemIds?.forEach(itemId => groupByItemId.set(itemId, group));
        });

    return groupByItemId;
}

function formatGroupItemCount(value: number) {
    return `${formatInteger(value)} ${value === 1 ? "item" : "items"}`;
}

function ExtractAndSaveAttributesModal(props: { open: boolean, itemCount: number, rqbQuery: any, onClose: () => void }) {
    const vulSourceItemService = useContext(VulSourceItemServiceContext);
    const [form] = useForm();
    const [inProgress, setInProgress] = useState(false);

    return (
        <Modal open={props.open}
               title={"Mass extraction of attributes"}
               okText={"Extract"}
               cancelText={"Cancel"}
               okButtonProps={{disabled: inProgress}}
               mask={{closable: false}}
               onOk={() => {
                   form.validateFields().then((values: any) => {
                       const options = QueryOptions.newUnlimitedRqbQueryInstance(props.rqbQuery);

                       setInProgress(true);

                       vulSourceItemService.extractAndSaveAttributes(
                           options,
                           values.skipAuditLogs,
                           values.skipItemChangeLogs,
                           values.skipUpdateFindings,
                           values.skipAiExtraction
                       )
                           .then(() => {
                               message.success("Attributes extracted successfully.");

                               props.onClose();
                           })
                           .finally(() => setInProgress(false));
                   });
               }}
               onCancel={() => props.onClose()}
        >
            Do you really want to extract and save attributes for {props.itemCount} items?

            <ConfigProvider theme={{components: {Form: {itemMarginBottom: 8}}}}>
                <Form form={form} className={globalStyles["common__form"]} labelCol={{span: 16}}>
                    <Form.Item name="skipAuditLogs" label={"Skip audit logs"} valuePropName={"checked"} initialValue={true}>
                        <Checkbox/>
                    </Form.Item>

                    <Form.Item name="skipItemChangeLogs" label={"Skip item change logs"} valuePropName={"checked"} initialValue={true}>
                        <Checkbox/>
                    </Form.Item>

                    <Form.Item name="skipUpdateFindings" label={"Skip update of SBOM component findings"} valuePropName={"checked"} initialValue={true}>
                        <Checkbox/>
                    </Form.Item>

                    <Form.Item name="skipAiExtraction" label={"Skip AI extraction"} valuePropName={"checked"} initialValue={true}>
                        <Checkbox/>
                    </Form.Item>
                </Form>
            </ConfigProvider>

        </Modal>
    );
}

export default VulSourceItemListTable;
